import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../components/AuthContext";

const HANDOFF_TYPES = {
  PICKUP: { label: "Pickup", icon: "📦", color: "#10b981", bg: "#d1fae5" },
  DELIVERY: { label: "Delivery", icon: "🏁", color: "#3b82f6", bg: "#dbeafe" },
  TRANSFER: { label: "Transfer", icon: "🔄", color: "#f59e0b", bg: "#fef3c7" },
};

const STATUS_STYLES = {
  PENDING: { label: "Pending", color: "#f59e0b", bg: "#fef3c7" },
  COMPLETED: { label: "Completed", color: "#10b981", bg: "#d1fae5" },
  DISPUTED: { label: "Disputed", color: "#ef4444", bg: "#fee2e2" },
};

const CONDITION_OPTIONS = [
  { value: "GOOD", label: "Good Condition", color: "#10b981" },
  { value: "DAMAGED", label: "Damaged", color: "#ef4444" },
  { value: "SHORTAGE", label: "Shortage", color: "#f59e0b" },
  { value: "OVERAGE", label: "Overage", color: "#f97316" },
];

export default function QRHandoffs() {
  const { adminEmail } = useAuth();
  const [activeTab, setActiveTab] = useState("handoffs");
  const [handoffs, setHandoffs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHandoff, setSelectedHandoff] = useState(null);
  const [handoffDetail, setHandoffDetail] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanToken, setScanToken] = useState("");

  // Create form
  const [createForm, setCreateForm] = useState({ masterId: "", handoffType: "PICKUP", locationName: "", notes: "" });

  const headers = { "Content-Type": "application/json", "x-admin-email": adminEmail, "x-user-role": "BROKER_ADMIN" };

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [hRes, sRes, lRes] = await Promise.all([
        fetch("/api/qr-handoff/handoffs", { headers }).then(r => r.json()),
        fetch("/api/qr-handoff/stats", { headers }).then(r => r.json()),
        fetch("/api/qr-handoff/loads", { headers }).then(r => r.json()),
      ]);
      setHandoffs(hRes.handoffs || []);
      setStats(sRes);
      setLoads(lRes.loads || []);
    } catch (e) { console.error("Load error:", e); }
    finally { setLoading(false); }
  }

  async function createHandoff() {
    if (!createForm.masterId) return window.alert("Please select a load");
    try {
      const res = await fetch("/api/qr-handoff/handoffs", { method: "POST", headers, body: JSON.stringify(createForm) });
      const data = await res.json();
      if (data.handoffId) {
        setShowCreateModal(false);
        setCreateForm({ masterId: "", handoffType: "PICKUP", locationName: "", notes: "" });
        loadAll();
      }
    } catch (e) { window.alert("Error: " + e.message); }
  }

  async function generateQr(handoffId) {
    try {
      const res = await fetch(`/api/qr-handoff/handoffs/${handoffId}/generate-qr`, { method: "POST", headers, body: "{}" });
      const data = await res.json();
      setQrData(data);
      setShowQrModal(true);
    } catch (e) { console.error("QR error:", e); }
  }

  async function loadHandoffDetail(handoffId) {
    try {
      const res = await fetch(`/api/qr-handoff/handoffs/${handoffId}`, { headers });
      const data = await res.json();
      setHandoffDetail(data);
      setSelectedHandoff(handoffId);
    } catch (e) { console.error("Detail error:", e); }
  }

  async function scanQrCode() {
    if (!scanToken.trim()) return;
    try {
      const res = await fetch(`/api/qr-handoff/scan/${scanToken.trim()}`, { headers });
      const data = await res.json();
      setScanResult(data);
    } catch (e) { console.error("Scan error:", e); }
  }

  async function confirmHandoff(token, condition, name, notes) {
    try {
      const res = await fetch(`/api/qr-handoff/scan/${token}/confirm`, {
        method: "POST", headers,
        body: JSON.stringify({ cargoCondition: condition, confirmerName: name, confirmerRole: "Driver", notes }),
      });
      const data = await res.json();
      if (data.success) {
        setScanResult(null);
        setShowScanModal(false);
        setScanToken("");
        loadAll();
      }
    } catch (e) { window.alert("Error: " + e.message); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading QR handoff system...</div>;

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 28 }}>📱</span> QR Handoff System
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: 14 }}>
            QR-verified pickup, delivery, and transfer confirmations
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowScanModal(true)} style={{ padding: "10px 20px", background: "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
            📷 Scan QR
          </button>
          <button onClick={() => setShowCreateModal(true)} style={{ padding: "10px 20px", background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
            ➕ New Handoff
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Handoffs", value: stats.totalHandoffs, icon: "📋", bg: "linear-gradient(135deg, #1e3a8a, #3b82f6)" },
            { label: "Pending", value: stats.pending, icon: "⏳", bg: "linear-gradient(135deg, #92400e, #f59e0b)" },
            { label: "Completed", value: stats.completed, icon: "✅", bg: "linear-gradient(135deg, #065f46, #10b981)" },
            { label: "Active QR Codes", value: stats.activeTokens, icon: "📱", bg: "linear-gradient(135deg, #7c3aed, #a78bfa)" },
            { label: "Last 24h", value: stats.completedLast24h, icon: "🕐", bg: "linear-gradient(135deg, #0f766e, #14b8a6)" },
          ].map((s, i) => (
            <div key={i} style={{ background: s.bg, borderRadius: 12, padding: 20, color: "white" }}>
              <div style={{ fontSize: 32, fontWeight: 900 }}>{s.value}</div>
              <div style={{ opacity: 0.8, fontSize: 13 }}>{s.icon} {s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {[
          { id: "handoffs", label: "📋 Handoffs" },
          { id: "detail", label: "🔍 Detail View" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "8px 16px", background: activeTab === tab.id ? "#3b82f6" : "white",
            color: activeTab === tab.id ? "white" : "#6b7280", border: "1px solid #e5e7eb",
            borderRadius: 8, fontWeight: 600, cursor: "pointer",
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Handoffs List */}
      {activeTab === "handoffs" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          {handoffs.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
              <div>No handoffs yet. Create a pickup or delivery handoff to get started.</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 12, color: "#6b7280" }}>Load</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 12, color: "#6b7280" }}>Type</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 12, color: "#6b7280" }}>Location</th>
                  <th style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#6b7280" }}>Status</th>
                  <th style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#6b7280" }}>QR Verified</th>
                  <th style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#6b7280" }}>Condition</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 12, color: "#6b7280" }}>Created</th>
                  <th style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#6b7280" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {handoffs.map(h => {
                  const typeInfo = HANDOFF_TYPES[h.handoffType] || { label: h.handoffType, icon: "📋", color: "#6b7280", bg: "#f3f4f6" };
                  const statusInfo = STATUS_STYLES[h.status] || { label: h.status, color: "#6b7280", bg: "#f3f4f6" };
                  return (
                    <tr key={h.handoffId} style={{ borderTop: "1px solid #f3f4f6" }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{h.loadNumber || "N/A"}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>{h.route}</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: typeInfo.bg, color: typeInfo.color, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: 12, fontSize: 13 }}>{h.locationName || "-"}</td>
                      <td style={{ padding: 12, textAlign: "center" }}>
                        <span style={{ padding: "3px 10px", background: statusInfo.bg, color: statusInfo.color, borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: "center", fontSize: 16 }}>
                        {h.qrVerified ? "✅" : "⬜"}
                      </td>
                      <td style={{ padding: 12, textAlign: "center", fontSize: 12 }}>
                        {h.cargoCondition ? (
                          <span style={{ padding: "2px 8px", background: h.cargoCondition === "GOOD" ? "#d1fae5" : "#fee2e2", color: h.cargoCondition === "GOOD" ? "#059669" : "#dc2626", borderRadius: 4, fontWeight: 600 }}>
                            {h.cargoCondition}
                          </span>
                        ) : "-"}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: "#6b7280" }}>{h.createdAt ? new Date(h.createdAt).toLocaleDateString() : "-"}</td>
                      <td style={{ padding: 12, textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                          <button onClick={() => { loadHandoffDetail(h.handoffId); setActiveTab("detail"); }} style={{ padding: "4px 8px", background: "#f3f4f6", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>View</button>
                          {h.status === "PENDING" && (
                            <button onClick={() => generateQr(h.handoffId)} style={{ padding: "4px 8px", background: "#dbeafe", color: "#1d4ed8", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>QR Code</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail View */}
      {activeTab === "detail" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Handoff Details */}
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 16 }}>📋 Handoff Details</h3>
            {handoffDetail ? (
              <>
                {(() => {
                  const hd = handoffDetail.handoff;
                  const typeInfo = HANDOFF_TYPES[hd.handoffType] || { label: hd.handoffType, icon: "📋", color: "#6b7280", bg: "#f3f4f6" };
                  return (
                    <>
                      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                        <span style={{ padding: "4px 12px", background: typeInfo.bg, color: typeInfo.color, borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                        <span style={{ padding: "4px 12px", background: (STATUS_STYLES[hd.status] || {}).bg || "#f3f4f6", color: (STATUS_STYLES[hd.status] || {}).color || "#6b7280", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                          {hd.status}
                        </span>
                        {hd.qrVerified && <span style={{ padding: "4px 12px", background: "#d1fae5", color: "#059669", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>✅ QR Verified</span>}
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                        <div><div style={{ fontSize: 11, color: "#6b7280" }}>Load Number</div><div style={{ fontWeight: 700 }}>{hd.loadNumber || "N/A"}</div></div>
                        <div><div style={{ fontSize: 11, color: "#6b7280" }}>Route</div><div style={{ fontWeight: 600, fontSize: 13 }}>{hd.route}</div></div>
                        <div><div style={{ fontSize: 11, color: "#6b7280" }}>Commodity</div><div style={{ fontWeight: 600 }}>{hd.commodity || "N/A"}</div></div>
                        <div><div style={{ fontSize: 11, color: "#6b7280" }}>Location</div><div style={{ fontWeight: 600 }}>{hd.locationName || "N/A"}</div></div>
                      </div>

                      {(hd.releasingCarrier || hd.receivingCarrier) && (
                        <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#1e3a8a", marginBottom: 8 }}>CARRIERS</div>
                          {hd.releasingCarrier && <div style={{ fontSize: 13 }}>Releasing: <strong>{hd.releasingCarrier}</strong></div>}
                          {hd.receivingCarrier && <div style={{ fontSize: 13 }}>Receiving: <strong>{hd.receivingCarrier}</strong></div>}
                        </div>
                      )}

                      {hd.cargoCondition && (
                        <div style={{ background: hd.cargoCondition === "GOOD" ? "#f0fdf4" : "#fef2f2", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>CARGO CONDITION</div>
                          <div style={{ fontWeight: 700, color: hd.cargoCondition === "GOOD" ? "#059669" : "#dc2626" }}>{hd.cargoCondition}</div>
                          {hd.conditionNotes && <div style={{ fontSize: 13, marginTop: 4 }}>{hd.conditionNotes}</div>}
                        </div>
                      )}

                      {hd.receivingSignature && (
                        <div style={{ background: "#f0fdf4", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>SIGNATURE</div>
                          <div style={{ fontSize: 13 }}>Signed by: <strong>{hd.receivingSignature.name}</strong> ({hd.receivingSignature.role})</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>{hd.receivingSignature.timestamp ? new Date(hd.receivingSignature.timestamp).toLocaleString() : ""}</div>
                        </div>
                      )}

                      {hd.sealNumber && <div style={{ fontSize: 13, marginBottom: 4 }}>Seal #: <strong>{hd.sealNumber}</strong></div>}
                      {hd.newSealNumber && <div style={{ fontSize: 13, marginBottom: 4 }}>New Seal #: <strong>{hd.newSealNumber}</strong></div>}

                      {hd.status === "PENDING" && (
                        <button onClick={() => generateQr(hd.handoffId)} style={{ marginTop: 12, padding: "10px 20px", background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", width: "100%" }}>
                          📱 Generate QR Code
                        </button>
                      )}
                    </>
                  );
                })()}
              </>
            ) : (
              <div style={{ color: "#9ca3af", textAlign: "center", padding: 20 }}>Select a handoff to view details</div>
            )}
          </div>

          {/* QR Tokens */}
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 16 }}>📱 QR Tokens</h3>
            {handoffDetail?.qrTokens && handoffDetail.qrTokens.length > 0 ? (
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {handoffDetail.qrTokens.map((t, i) => (
                  <div key={t.tokenId} style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${t.status === "USED" ? "#d1fae5" : t.status === "ACTIVE" ? "#dbeafe" : "#e5e7eb"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>Token #{i + 1}</span>
                      <span style={{ padding: "2px 8px", background: t.status === "USED" ? "#d1fae5" : t.status === "ACTIVE" ? "#dbeafe" : "#fee2e2", color: t.status === "USED" ? "#059669" : t.status === "ACTIVE" ? "#1d4ed8" : "#dc2626", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{t.status}</span>
                    </div>
                    <div style={{ fontSize: 11, fontFamily: "monospace", color: "#6b7280" }}>{t.token}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                      Created: {t.createdAt ? new Date(t.createdAt).toLocaleString() : ""}
                      {t.usedAt && <> | Used: {new Date(t.usedAt).toLocaleString()}</>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#9ca3af", textAlign: "center", padding: 20 }}>No QR tokens generated yet</div>
            )}

            {/* Quick select */}
            <div style={{ marginTop: 16, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Select handoff:</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {handoffs.map(h => (
                  <button key={h.handoffId} onClick={() => loadHandoffDetail(h.handoffId)} style={{
                    padding: "4px 10px", background: selectedHandoff === h.handoffId ? "#3b82f6" : "#f3f4f6",
                    color: selectedHandoff === h.handoffId ? "white" : "#374151", border: "none", borderRadius: 6,
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>
                    {h.loadNumber || "?"} - {h.handoffType}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== QR CODE MODAL ========== */}
      {showQrModal && qrData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowQrModal(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 32, width: 420, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>📱 QR Code Generated</div>
            <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>{qrData.instructions}</div>

            {/* QR Image */}
            <div style={{ background: "white", border: "3px solid #1e3a8a", borderRadius: 16, padding: 16, display: "inline-block", marginBottom: 16 }}>
              <img src={qrData.qrImageUrl} alt="QR Code" style={{ width: 250, height: 250 }} />
            </div>

            {/* Handoff Info */}
            <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12, marginBottom: 16, textAlign: "left" }}>
              <div style={{ fontSize: 13 }}>Load: <strong>{qrData.handoff?.loadNumber}</strong></div>
              <div style={{ fontSize: 13 }}>Type: <strong>{qrData.handoff?.type}</strong></div>
              {qrData.handoff?.location && <div style={{ fontSize: 13 }}>Location: <strong>{qrData.handoff.location}</strong></div>}
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Expires: {qrData.expiresAt ? new Date(qrData.expiresAt).toLocaleString() : "N/A"}</div>
            </div>

            {/* Scan URL */}
            <div style={{ background: "#f9fafb", borderRadius: 8, padding: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#6b7280" }}>Scan URL</div>
              <div style={{ fontSize: 11, fontFamily: "monospace", wordBreak: "break-all" }}>{qrData.qrData}</div>
            </div>

            <button onClick={() => setShowQrModal(false)} style={{ width: "100%", padding: 12, background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* ========== SCAN QR MODAL ========== */}
      {showScanModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => { setShowScanModal(false); setScanResult(null); setScanToken(""); }}>
          <div style={{ background: "white", borderRadius: 16, padding: 24, width: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>📷 Scan / Enter QR Token</h3>

            {!scanResult ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>QR Token</label>
                  <input value={scanToken} onChange={e => setScanToken(e.target.value)} placeholder="Paste or enter QR token..." style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => { setShowScanModal(false); setScanToken(""); }} style={{ flex: 1, padding: 12, background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                  <button onClick={scanQrCode} style={{ flex: 1, padding: 12, background: "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Verify Token</button>
                </div>
              </>
            ) : (
              <ScanConfirmForm scanResult={scanResult} token={scanToken} onConfirm={confirmHandoff} onCancel={() => { setScanResult(null); setScanToken(""); }} />
            )}
          </div>
        </div>
      )}

      {/* ========== CREATE HANDOFF MODAL ========== */}
      {showCreateModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowCreateModal(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 24, width: 450 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px 0" }}>➕ Create Handoff</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Select Load</label>
              <select value={createForm.masterId} onChange={e => setCreateForm(p => ({ ...p, masterId: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <option value="">-- Select a load --</option>
                {loads.map(l => (
                  <option key={l.id} value={l.id}>{l.loadNumber || l.id.slice(-8)} — {l.origin} → {l.destination}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Handoff Type</label>
              <div style={{ display: "flex", gap: 8 }}>
                {Object.entries(HANDOFF_TYPES).map(([key, val]) => (
                  <button key={key} onClick={() => setCreateForm(p => ({ ...p, handoffType: key }))} style={{
                    flex: 1, padding: "10px 8px", background: createForm.handoffType === key ? val.bg : "#f9fafb",
                    color: createForm.handoffType === key ? val.color : "#6b7280",
                    border: createForm.handoffType === key ? `2px solid ${val.color}` : "1px solid #e5e7eb",
                    borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
                  }}>
                    {val.icon} {val.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Location Name</label>
              <input value={createForm.locationName} onChange={e => setCreateForm(p => ({ ...p, locationName: e.target.value }))} placeholder="e.g., Houston Terminal, Well Pad 7" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Notes</label>
              <textarea value={createForm.notes} onChange={e => setCreateForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Optional notes..." style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowCreateModal(false)} style={{ flex: 1, padding: 12, background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={createHandoff} style={{ flex: 1, padding: 12, background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SCAN CONFIRM SUB-COMPONENT
// ============================================================================

function ScanConfirmForm({ scanResult, token, onConfirm, onCancel }) {
  const [condition, setCondition] = useState("GOOD");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  if (!scanResult.valid) {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{scanResult.message}</div>
        <button onClick={onCancel} style={{ padding: "10px 24px", background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Back</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>QR Code Valid</div>
      </div>

      <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13 }}>Load: <strong>{scanResult.loadNumber}</strong></div>
        <div style={{ fontSize: 13 }}>Type: <strong>{scanResult.handoffType}</strong></div>
        <div style={{ fontSize: 13 }}>Route: {scanResult.route}</div>
        {scanResult.location && <div style={{ fontSize: 13 }}>Location: {scanResult.location}</div>}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Cargo Condition</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {CONDITION_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setCondition(opt.value)} style={{
              padding: "8px", background: condition === opt.value ? `${opt.color}20` : "#f9fafb",
              color: condition === opt.value ? opt.color : "#6b7280",
              border: condition === opt.value ? `2px solid ${opt.color}` : "1px solid #e5e7eb",
              borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 12,
            }}>{opt.label}</button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Your Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Driver / receiver name" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Any notes about the handoff..." style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", resize: "vertical", boxSizing: "border-box" }} />
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: 12, background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        <button onClick={() => onConfirm(token, condition, name, notes)} style={{ flex: 1, padding: 12, background: "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
          ✅ Confirm Handoff
        </button>
      </div>
    </>
  );
}
